import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import MonthlyClosesPanel, { type ClosePoint } from '@/components/business-value/MonthlyClosesPanel'
import {
  Landmark, Building, Package, CreditCard, Truck, FileText, MoreHorizontal,
  Loader2, RefreshCw, Camera, TrendingUp, TrendingDown, Minus, Plus,
  Pencil, Trash2, X, Save, Database, ChevronDown, ChevronUp, Users, ArrowDownUp, Scale,
} from 'lucide-react'

// ==================== Types ====================

type BreakdownRow = Record<string, string | number>

interface BreakdownColumn {
  key: string
  label: string
}

interface Component {
  key: string
  label: string
  kind: 'asset' | 'liability'
  source: 'factusol' | 'manual' | 'ajuste'
  category?: string
  amount: number
  estimated?: boolean
  detail?: string
  updated_at?: string
  item_id?: number
  has_schedule?: boolean
  has_plan?: boolean
  breakdown?: BreakdownRow[]
  breakdown_columns?: BreakdownColumn[]
}

interface CurrentValue {
  computed_at: string
  factusol_cached_at: string
  components: Component[]
  total_assets: number
  total_liabilities: number
  net_value: number
}

interface Category {
  code: string
  name: string
  kind: 'asset' | 'liability'
  icon: string
  sort_order: number
}

interface Snapshot {
  id: number
  snapshot_date: string
  year_month: string
  source: string
  total_assets: number
  total_liabilities: number
  net_value: number
}

// ==================== Helpers ====================

function fmtCur(v: number): string {
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v)
}

function fmtCurFull(v: number): string {
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(v)
}

function fmtDate(iso: string): string {
  if (!iso || iso.length < 10) return iso || '—'
  const [y, m, d] = iso.slice(0, 10).split('-')
  return `${d}/${m}/${y}`
}

function fmtMonth(ym: string): string {
  const [y, m] = ym.split('-')
  const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
  return `${months[parseInt(m, 10) - 1]} ${y.slice(2)}`
}

// Columnas de desglose que son importes en euros
const MONEY_COLUMNS = new Set(['amount', 'paid', 'rate'])

const iconMap: Record<string, React.ElementType> = {
  Building, Landmark, Package, CreditCard, Truck, FileText, MoreHorizontal, Users,
}

function CatIcon({ icon, className = 'w-4 h-4' }: { icon?: string; className?: string }) {
  const Icon = (icon && iconMap[icon]) || MoreHorizontal
  return <Icon className={className} />
}

// ==================== Page ====================

export default function BusinessValuePage() {
  const queryClient = useQueryClient()
  const [expandedKey, setExpandedKey] = useState<string | null>(null)
  const [movementFor, setMovementFor] = useState<number | null>(null)
  const [movType, setMovType] = useState<'retirada' | 'aportacion'>('retirada')
  const [movDate, setMovDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [movAmount, setMovAmount] = useState('')
  const [movNotes, setMovNotes] = useState('')
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editAmount, setEditAmount] = useState('')
  const [showAddForm, setShowAddForm] = useState<'asset' | 'liability' | null>(null)
  const [newCategory, setNewCategory] = useState('')
  const [newName, setNewName] = useState('')
  const [newAmount, setNewAmount] = useState('')
  const [newNotes, setNewNotes] = useState('')

  const { data: current, isLoading, isFetching, refetch } = useQuery<CurrentValue>({
    queryKey: ['business-value-current'],
    queryFn: async () => (await api.get('/business-value/current')).data,
    staleTime: 5 * 60 * 1000,
  })

  const { data: categoriesData } = useQuery<{ categories: Category[] }>({
    queryKey: ['business-value-categories'],
    queryFn: async () => (await api.get('/business-value/categories')).data,
    staleTime: Infinity,
  })

  const { data: closesData } = useQuery<{ closes: ClosePoint[] }>({
    queryKey: ['business-value-closes'],
    queryFn: async () => (await api.get('/business-value/closes')).data,
  })

  const { data: snapshotsData } = useQuery<{ snapshots: Snapshot[] }>({
    queryKey: ['business-value-snapshots'],
    queryFn: async () => (await api.get('/business-value/snapshots?limit=30')).data,
  })

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['business-value-current'] })
    queryClient.invalidateQueries({ queryKey: ['business-value-closes'] })
    queryClient.invalidateQueries({ queryKey: ['business-value-snapshots'] })
  }

  const refreshMutation = useMutation({
    mutationFn: async () => (await api.get('/business-value/current?refresh=true')).data,
    onSuccess: (data) => queryClient.setQueryData(['business-value-current'], data),
  })

  const snapshotMutation = useMutation({
    mutationFn: async () => (await api.post('/business-value/snapshot')).data,
    onSuccess: invalidateAll,
  })

  const createMutation = useMutation({
    mutationFn: async (body: { category: string; name: string; amount: number; notes?: string }) =>
      (await api.post('/business-value/items', body)).data,
    onSuccess: () => {
      setShowAddForm(null)
      setNewCategory(''); setNewName(''); setNewAmount(''); setNewNotes('')
      invalidateAll()
    },
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, amount }: { id: number; amount: number }) =>
      (await api.put(`/business-value/items/${id}`, { amount })).data,
    onSuccess: () => {
      setEditingId(null)
      invalidateAll()
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/business-value/items/${id}`)).data,
    onSuccess: invalidateAll,
  })

  const deleteSnapshotMutation = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/business-value/snapshots/${id}`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business-value-snapshots'] })
    },
  })

  const movementMutation = useMutation({
    mutationFn: async ({ id, date, amount, notes }: { id: number; date: string; amount: number; notes?: string }) =>
      (await api.post(`/business-value/items/${id}/movements`, { date, amount, notes })).data,
    onSuccess: () => {
      setMovementFor(null)
      setMovAmount(''); setMovNotes('')
      invalidateAll()
    },
  })

  const submitMovement = () => {
    const value = parseFloat(movAmount.replace(',', '.'))
    if (movementFor == null || isNaN(value) || value <= 0 || !movDate) return
    movementMutation.mutate({
      id: movementFor,
      date: movDate,
      amount: movType === 'retirada' ? -Math.abs(value) : Math.abs(value),
      notes: movNotes.trim() || undefined,
    })
  }

  const categories = categoriesData?.categories ?? []
  const snapshots = snapshotsData?.snapshots ?? []

  const lastClose = closesData?.closes?.length ? closesData.closes[closesData.closes.length - 1] : null
  const sinceClose = current && lastClose ? current.net_value - lastClose.net_value : null

  const assets = current?.components.filter(c => c.kind === 'asset') ?? []
  const liabilities = current?.components.filter(c => c.kind === 'liability') ?? []

  const startEdit = (c: Component) => {
    if (c.item_id == null) return
    setEditingId(c.item_id)
    setEditAmount(String(c.amount))
  }

  const submitEdit = () => {
    const value = parseFloat(editAmount.replace(',', '.'))
    if (editingId != null && !isNaN(value)) {
      updateMutation.mutate({ id: editingId, amount: value })
    }
  }

  const submitNew = () => {
    const value = parseFloat(newAmount.replace(',', '.'))
    if (!newCategory || !newName.trim() || isNaN(value)) return
    createMutation.mutate({
      category: newCategory,
      name: newName.trim(),
      amount: value,
      notes: newNotes.trim() || undefined,
    })
  }

  const renderComponentRow = (c: Component) => {
    const hasBreakdown = (c.breakdown?.length ?? 0) > 0
    const isExpanded = expandedKey === c.key
    return (
    <div key={c.key} className="border-b border-slate-100 dark:border-slate-700/50 last:border-0">
    <div className="flex items-center justify-between py-2.5">
      <div
        className={`flex items-center gap-2.5 min-w-0 ${hasBreakdown ? 'cursor-pointer select-none' : ''}`}
        onClick={hasBreakdown ? () => setExpandedKey(isExpanded ? null : c.key) : undefined}
        title={hasBreakdown ? (isExpanded ? 'Ocultar desglose' : 'Ver desglose') : undefined}>
        {c.source === 'factusol' ? (
          <span title="Calculado automáticamente desde Factusol">
            <Database className="w-4 h-4 text-blue-500 shrink-0" />
          </span>
        ) : c.source === 'ajuste' ? (
          <span title="Ajuste de corte calculado con Factusol y los extractos bancarios">
            <Scale className="w-4 h-4 text-amber-500 shrink-0" />
          </span>
        ) : (
          <CatIcon icon={categories.find(cat => cat.code === c.category)?.icon} className="w-4 h-4 text-slate-400 shrink-0" />
        )}
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">{c.label}</div>
          {c.detail && <div className="text-xs text-slate-400 truncate">{c.detail}</div>}
        </div>
        {c.source === 'factusol' && (
          <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300 shrink-0">auto</span>
        )}
        {c.source === 'ajuste' && (
          <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 shrink-0"
            title="Devengo: deuda o activo que ya existe aunque aún no se haya pagado o recibido">ajuste</span>
        )}
        {hasBreakdown && (
          isExpanded
            ? <ChevronUp className="w-4 h-4 text-slate-400 shrink-0" />
            : <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {editingId != null && editingId === c.item_id ? (
          <>
            <input
              type="text" inputMode="decimal" autoFocus
              value={editAmount}
              onChange={e => setEditAmount(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') submitEdit(); if (e.key === 'Escape') setEditingId(null) }}
              className="w-28 px-2 py-1 text-sm text-right border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800"
            />
            <button onClick={submitEdit} className="p-1 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30 rounded" title="Guardar">
              {updateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            </button>
            <button onClick={() => setEditingId(null)} className="p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded" title="Cancelar">
              <X className="w-4 h-4" />
            </button>
          </>
        ) : (
          <>
            <span className="text-sm font-semibold tabular-nums text-slate-800 dark:text-slate-100">{fmtCurFull(c.amount)}</span>
            {c.source === 'manual' && c.item_id != null && (
              <>
                {c.has_plan ? (
                  <button
                    onClick={() => { setMovementFor(movementFor === c.item_id ? null : c.item_id!); setMovType('retirada') }}
                    className="p-1 text-slate-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded"
                    title="Registrar retirada o aportación extra">
                    <ArrowDownUp className="w-3.5 h-3.5" />
                  </button>
                ) : !c.has_schedule && (
                  <button onClick={() => startEdit(c)} className="p-1 text-slate-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded" title="Editar importe">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
                <button
                  onClick={() => { if (confirm(`¿Eliminar "${c.label}"?`)) deleteMutation.mutate(c.item_id!) }}
                  className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded" title="Eliminar">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </>
            )}
          </>
        )}
      </div>
    </div>
    {c.has_plan && movementFor === c.item_id && (
      <div className="mb-2.5 ml-6 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/60 space-y-2">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <select
            value={movType} onChange={e => setMovType(e.target.value as 'retirada' | 'aportacion')}
            className="px-2 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800">
            <option value="retirada">Retirada</option>
            <option value="aportacion">Aportación extra</option>
          </select>
          <input
            type="date" value={movDate} onChange={e => setMovDate(e.target.value)}
            className="px-2 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800"
          />
          <input
            type="text" inputMode="decimal" placeholder="Importe €" value={movAmount}
            onChange={e => setMovAmount(e.target.value)}
            className="px-2 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800"
          />
          <input
            type="text" placeholder="Nota (opcional)" value={movNotes}
            onChange={e => setMovNotes(e.target.value)}
            className="px-2 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800"
          />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-400">
            El saldo y los intereses futuros se recalculan automáticamente.
          </span>
          <div className="flex gap-2">
            <button onClick={() => setMovementFor(null)} className="px-3 py-1.5 text-sm text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 rounded">
              Cancelar
            </button>
            <button
              onClick={submitMovement}
              disabled={movementMutation.isPending || !movAmount || !movDate}
              className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 rounded flex items-center gap-1.5">
              {movementMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Registrar
            </button>
          </div>
        </div>
      </div>
    )}
    {hasBreakdown && isExpanded && (
      <div className="mb-2.5 ml-6 rounded-lg bg-slate-50 dark:bg-slate-800/60 overflow-x-auto max-h-80 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800">
            <tr className="text-left text-slate-400 uppercase border-b border-slate-200 dark:border-slate-700">
              {(c.breakdown_columns ?? []).map(col => (
                <th key={col.key} className={`py-1.5 px-3 font-medium ${MONEY_COLUMNS.has(col.key) || col.key === 'months' ? 'text-right' : ''}`}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {c.breakdown!.map((row, i) => (
              <tr key={i} className="border-b border-slate-100 dark:border-slate-700/50 last:border-0">
                {(c.breakdown_columns ?? []).map(col => {
                  const value = row[col.key]
                  if (MONEY_COLUMNS.has(col.key) || col.key === 'months') {
                    return (
                      <td key={col.key} className="py-1.5 px-3 text-right tabular-nums font-medium text-slate-700 dark:text-slate-200 whitespace-nowrap">
                        {col.key === 'months' ? String(value ?? '') : fmtCurFull(Number(value) || 0)}
                      </td>
                    )
                  }
                  if (col.key === 'date') {
                    return (
                      <td key={col.key} className="py-1.5 px-3 text-slate-500 dark:text-slate-400 whitespace-nowrap">
                        {fmtDate(String(value ?? ''))}
                      </td>
                    )
                  }
                  if (col.key === 'type') {
                    return (
                      <td key={col.key} className="py-1.5 px-3">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap ${
                          value === 'Rectificativa' ? 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                          : value === 'Retirada' ? 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                          : value === 'Aportación extra' ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                          : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400'
                        }`}>{String(value ?? '')}</span>
                      </td>
                    )
                  }
                  if (col.key === 'status') {
                    return (
                      <td key={col.key} className="py-1.5 px-3">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap ${
                          value === 'Recibido parcial'
                            ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                            : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                        }`}>{String(value ?? '')}</span>
                      </td>
                    )
                  }
                  return (
                    <td key={col.key} className="py-1.5 px-3 text-slate-600 dark:text-slate-300">
                      {String(value ?? '')}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
    </div>
    )
  }

  const renderAddForm = (kind: 'asset' | 'liability') => (
    <div className="mt-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/60 space-y-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <select
          value={newCategory} onChange={e => setNewCategory(e.target.value)}
          className="px-2 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800">
          <option value="">Categoría…</option>
          {categories.filter(c => c.kind === kind).map(c => (
            <option key={c.code} value={c.code}>{c.name}</option>
          ))}
        </select>
        <input
          type="text" placeholder="Nombre (ej: Local calle Mayor)" value={newName}
          onChange={e => setNewName(e.target.value)}
          className="px-2 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800"
        />
        <input
          type="text" inputMode="decimal" placeholder="Importe €" value={newAmount}
          onChange={e => setNewAmount(e.target.value)}
          className="px-2 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800"
        />
        <input
          type="text" placeholder="Notas (opcional)" value={newNotes}
          onChange={e => setNewNotes(e.target.value)}
          className="px-2 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800"
        />
      </div>
      <div className="flex gap-2 justify-end">
        <button onClick={() => setShowAddForm(null)} className="px-3 py-1.5 text-sm text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 rounded">
          Cancelar
        </button>
        <button
          onClick={submitNew}
          disabled={createMutation.isPending || !newCategory || !newName.trim() || !newAmount}
          className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 rounded flex items-center gap-1.5">
          {createMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
          Añadir
        </button>
      </div>
    </div>
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        Calculando valor del negocio (consultando Factusol)…
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <Landmark className="w-6 h-6 text-primary-600" />
            Valor del negocio
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Patrimonio neto: activos (stock, pendientes, banco, inmuebles) menos deudas, con cierre mensual comparable
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => refreshMutation.mutate()}
            disabled={refreshMutation.isPending || isFetching}
            className="px-3 py-2 text-sm font-medium border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
            <RefreshCw className={`w-4 h-4 ${refreshMutation.isPending ? 'animate-spin' : ''}`} />
            Recalcular
          </button>
          <button
            onClick={() => snapshotMutation.mutate()}
            disabled={snapshotMutation.isPending}
            title="Guarda el valor de este momento. Para comparar meses usa los cierres mensuales."
            className="px-3 py-2 text-sm font-medium border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
            {snapshotMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
            Foto puntual
          </button>
        </div>
      </div>

      {/* Hero + stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card p-5 md:col-span-1">
          <div className="text-sm text-slate-500">Valor neto hoy (estimado)</div>
          <div className={`text-3xl font-bold tabular-nums mt-1 ${(current?.net_value ?? 0) >= 0 ? 'text-slate-800 dark:text-slate-100' : 'text-red-600'}`}>
            {current ? fmtCurFull(current.net_value) : '—'}
          </div>
          {sinceClose != null && lastClose && (
            <div className={`flex items-center gap-1 text-sm mt-2 ${sinceClose > 0 ? 'text-green-600' : sinceClose < 0 ? 'text-red-600' : 'text-slate-500'}`}>
              {sinceClose > 0 ? <TrendingUp className="w-4 h-4" /> : sinceClose < 0 ? <TrendingDown className="w-4 h-4" /> : <Minus className="w-4 h-4" />}
              {sinceClose > 0 ? '+' : ''}{fmtCurFull(sinceClose)}
              <span className="text-slate-400 ml-1">desde el cierre de {fmtMonth(lastClose.year_month)}</span>
            </div>
          )}
          <div className="text-xs text-slate-400 mt-1">
            Orientativo: el banco es el del último extracto. Para comparar, usa los cierres.
          </div>
          <div className="text-xs text-slate-400 mt-2">
            Datos Factusol de {current ? new Date(current.factusol_cached_at).toLocaleString('es-ES', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' }) : '—'}
          </div>
        </div>
        <div className="card p-5">
          <div className="text-sm text-slate-500">Activos</div>
          <div className="text-2xl font-bold tabular-nums text-green-700 dark:text-green-400 mt-1">
            {current ? fmtCurFull(current.total_assets) : '—'}
          </div>
          <div className="text-xs text-slate-400 mt-2">{assets.length} partidas</div>
        </div>
        <div className="card p-5">
          <div className="text-sm text-slate-500">Deudas</div>
          <div className="text-2xl font-bold tabular-nums text-red-700 dark:text-red-400 mt-1">
            {current ? fmtCurFull(-current.total_liabilities) : '—'}
          </div>
          <div className="text-xs text-slate-400 mt-2">{liabilities.length} partidas</div>
        </div>
      </div>

      <MonthlyClosesPanel />

      {/* Breakdown: assets & liabilities */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="card p-5">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Activos</h3>
            <button
              onClick={() => { setShowAddForm(showAddForm === 'asset' ? null : 'asset'); setNewCategory('') }}
              className="text-xs font-medium text-primary-600 hover:text-primary-700 flex items-center gap-1">
              <Plus className="w-3.5 h-3.5" /> Añadir activo
            </button>
          </div>
          {assets.map(renderComponentRow)}
          {assets.length === 0 && <div className="text-sm text-slate-400 py-3">Sin activos</div>}
          {showAddForm === 'asset' && renderAddForm('asset')}
        </div>
        <div className="card p-5">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Deudas pendientes de pago</h3>
            <button
              onClick={() => { setShowAddForm(showAddForm === 'liability' ? null : 'liability'); setNewCategory('') }}
              className="text-xs font-medium text-primary-600 hover:text-primary-700 flex items-center gap-1">
              <Plus className="w-3.5 h-3.5" /> Añadir deuda
            </button>
          </div>
          {liabilities.map(renderComponentRow)}
          {liabilities.length === 0 && (
            <div className="text-sm text-slate-400 py-3">
              Sin deudas registradas. Añade préstamos, facturas de proveedor pendientes o impuestos.
            </div>
          )}
          {showAddForm === 'liability' && renderAddForm('liability')}
        </div>
      </div>

      {/* Saved snapshots */}
      {snapshots.length > 0 && (
        <div className="card p-5">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
            Fotos puntuales ({snapshots.length})
          </h3>
          <div className="overflow-x-auto max-h-80 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white dark:bg-slate-900">
                <tr className="text-left text-xs text-slate-400 uppercase border-b border-slate-200 dark:border-slate-700">
                  <th className="py-2 pr-4">Fecha</th>
                  <th className="py-2 pr-4">Origen</th>
                  <th className="py-2 pr-4 text-right">Activos</th>
                  <th className="py-2 pr-4 text-right">Deudas</th>
                  <th className="py-2 pr-4 text-right">Valor neto</th>
                  <th className="py-2 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {snapshots.map(s => (
                  <tr key={s.id} className="border-b border-slate-100 dark:border-slate-700/50 last:border-0">
                    <td className="py-2 pr-4 font-medium text-slate-700 dark:text-slate-200 whitespace-nowrap">{fmtDate(s.snapshot_date)}</td>
                    <td className="py-2 pr-4">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        s.source === 'auto'
                          ? 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400'
                          : 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300'
                      }`}>{s.source === 'auto' ? 'Automática' : 'Manual'}</span>
                    </td>
                    <td className="py-2 pr-4 text-right tabular-nums">{fmtCurFull(s.total_assets)}</td>
                    <td className="py-2 pr-4 text-right tabular-nums text-red-600 dark:text-red-400">{fmtCurFull(-s.total_liabilities)}</td>
                    <td className="py-2 pr-4 text-right tabular-nums font-semibold">{fmtCurFull(s.net_value)}</td>
                    <td className="py-2 text-right">
                      <button
                        onClick={() => {
                          if (confirm(`¿Borrar la foto del ${fmtDate(s.snapshot_date)} (valor neto ${fmtCurFull(s.net_value)})? Esta acción no se puede deshacer.`)) {
                            deleteSnapshotMutation.mutate(s.id)
                          }
                        }}
                        disabled={deleteSnapshotMutation.isPending}
                        className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                        title="Borrar esta foto">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
